import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Tag, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { getCategories, addCategory, updateCategory, deleteCategory, getUserStructures } from '../../api';

const CategoryManagement = () => {
  const [structures, setStructures] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // { name, structures: [{id, categoryId}] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [editMode, setEditMode] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    fetchStructures();
  }, []);

  const fetchStructures = async () => {
    try {
      const response = await getUserStructures();
      setStructures(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des structures');
      console.error(err);
    }
  };

  // Charger les catégories de TOUTES les structures et fusionner
  const fetchAllCategories = useCallback(async () => {
    if (structures.length === 0) return;
    try {
      setLoading(true);
      const results = await Promise.all(
        structures.map(s => getCategories(s.id).then(res => ({ structureId: s.id, categories: res.data })))
      );

      // Fusionner : regrouper par nom de catégorie
      const categoryMap = new Map();
      results.forEach(({ structureId, categories }) => {
        categories.forEach(cat => {
          const key = cat.name.toLowerCase();
          if (!categoryMap.has(key)) {
            categoryMap.set(key, { name: cat.name, structures: [] });
          }
          categoryMap.get(key).structures.push({ structureId, categoryId: cat.id });
        });
      });

      // Trier par nom
      const merged = Array.from(categoryMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name, 'fr')
      );
      setAllCategories(merged);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des catégories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [structures]);

  useEffect(() => {
    if (structures.length > 0) {
      fetchAllCategories();
    }
  }, [structures, fetchAllCategories]);

  // Ajouter une catégorie sur toutes les structures cochées
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    try {
      setLoading(true);
      setError(null);
      // Créer sur toutes les structures par défaut
      let created = 0;
      let duplicates = 0;
      for (const s of structures) {
        try {
          await addCategory({ name: newCategory, structureId: s.id });
          created++;
        } catch (err) {
          if (err.response?.status === 409 || err.response?.data?.error?.includes('unique')) {
            duplicates++;
          } else {
            throw err;
          }
        }
      }
      if (duplicates > 0 && created > 0) {
        setError(`Catégorie créée sur ${created} structure(s). Existait déjà sur ${duplicates}.`);
      }
      setNewCategory('');
      setShowForm(false);
      fetchAllCategories();
    } catch (err) {
      setError("Erreur lors de l'ajout de la catégorie");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Cocher/décocher une structure pour une catégorie
  const toggleCategoryOnStructure = async (categoryName, structureId, isChecked) => {
    try {
      setLoading(true);
      setError(null);
      if (isChecked) {
        // Supprimer la catégorie de cette structure
        const cat = allCategories.find(c => c.name === categoryName);
        const entry = cat?.structures.find(s => s.structureId === structureId);
        if (entry) {
          await deleteCategory(entry.categoryId, structureId);
        }
      } else {
        // Ajouter la catégorie sur cette structure
        try {
          await addCategory({ name: categoryName, structureId });
        } catch (err) {
          if (err.response?.status === 409 || err.response?.data?.error?.includes('unique')) {
            // Déjà existante, ignorer
          } else {
            throw err;
          }
        }
      }
      fetchAllCategories();
    } catch (err) {
      setError('Erreur lors de la mise à jour');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (categoryName) => {
    setEditMode(categoryName);
    setEditValue(categoryName);
  };

  const cancelEdit = () => {
    setEditMode(null);
    setEditValue('');
  };

  const saveEdit = async (oldName) => {
    if (!editValue.trim() || editValue === oldName) {
      cancelEdit();
      return;
    }
    try {
      setLoading(true);
      setError(null);
      // Renommer sur toutes les structures où elle existe
      const cat = allCategories.find(c => c.name === oldName);
      if (cat) {
        for (const entry of cat.structures) {
          await updateCategory(entry.categoryId, { name: editValue, structureId: entry.structureId });
        }
      }
      cancelEdit();
      fetchAllCategories();
    } catch (err) {
      setError('Erreur lors du renommage');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async (categoryName) => {
    const cat = allCategories.find(c => c.name === categoryName);
    const count = cat?.structures.length || 0;
    if (!window.confirm(`Supprimer "${categoryName}" de ${count} structure(s) ?\nLes produits associés perdront leur catégorie.`)) return;

    try {
      setLoading(true);
      setError(null);
      for (const entry of cat.structures) {
        try {
          await deleteCategory(entry.categoryId, entry.structureId);
        } catch (err) {
          // Continuer malgré les erreurs individuelles
          console.error(`Erreur suppression sur structure ${entry.structureId}:`, err);
        }
      }
      fetchAllCategories();
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && structures.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent>
            <p className="text-gray-500 py-8 text-center">Chargement...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag size={20} />
            Gestion des catégories
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
              {error}
            </div>
          )}

          {showForm && (
            <form onSubmit={handleAddCategory} className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Nom de la nouvelle catégorie"
                  disabled={loading}
                  required
                  autoFocus
                />
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50"
                  disabled={loading || !newCategory.trim()}
                >
                  <Check size={18} />
                  Créer sur toutes
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setNewCategory(''); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition"
                >
                  Annuler
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                La catégorie sera créée sur toutes les structures. Vous pouvez ensuite décocher celles qui ne sont pas concernées.
              </p>
            </form>
          )}

          {/* Tableau des catégories avec checkboxes structures */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-3">
              Catégories ({allCategories.length})
            </h3>
            {allCategories.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">Aucune catégorie trouvée</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Catégorie</th>
                      {structures.map(s => (
                        <th key={s.id} className="text-center px-2 py-2 text-sm font-semibold text-gray-700 whitespace-nowrap">
                          {s.name}
                        </th>
                      ))}
                      <th className="text-center px-2 py-2 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCategories.map((cat) => (
                      <tr key={cat.name} className="border-t hover:bg-gray-50 transition">
                        <td className="px-3 py-2">
                          {editMode === cat.name ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                className="flex-1 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit(cat.name);
                                  if (e.key === 'Escape') cancelEdit();
                                }}
                                autoFocus
                              />
                              <button
                                onClick={() => saveEdit(cat.name)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded-full transition"
                                title="Enregistrer"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="p-1 text-gray-500 hover:bg-gray-200 rounded-full transition"
                                title="Annuler"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-800 font-medium">{cat.name}</span>
                          )}
                        </td>
                        {structures.map(s => {
                          const isChecked = cat.structures.some(cs => cs.structureId === s.id);
                          return (
                            <td key={s.id} className="text-center px-2 py-2">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleCategoryOnStructure(cat.name, s.id, isChecked)}
                                className="rounded cursor-pointer w-4 h-4"
                                disabled={loading}
                              />
                            </td>
                          );
                        })}
                        <td className="text-center px-2 py-2">
                          <div className="flex justify-center gap-1">
                            <button
                              onClick={() => startEdit(cat.name)}
                              className="p-1 text-primary hover:bg-primary/5 rounded-full transition"
                              title="Renommer"
                              disabled={loading}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteAll(cat.name)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded-full transition"
                              title="Supprimer de toutes les structures"
                              disabled={loading}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter une catégorie"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default CategoryManagement;
